import { useState } from 'react'
import type { EventPageClass, EventPageEvent, EventPageViewer, SignupState } from '../data/types'
import { truthy } from '../data/types'
import { sanitizeRichText } from '../data/richText'
import { formatMoney } from '../data/format'
import { postRemoveClassEntry } from '../data/eventPageApi'
import { entryStatusSummary, unpaidEntryLabel } from '../data/signupSummary'
import { VehicleSignupModal } from './VehicleSignupModal'
import styles from './ClassList.module.css'

type ActiveModal = { cls: EventPageClass; mode: 'add' | 'change'; currentVehicleId?: number; currentAmountDue?: number | string }

/** Whether the racer has already entered this class - shared by the row highlight below and the
 *  checkbox/car-pill components, which each look it up again themselves (cheap, avoids threading
 *  an extra prop through). */
function isEntered(cls: EventPageClass, signup: SignupState | null): boolean {
  if (!signup) return false
  const row = signup.classes.find((c) => c.id === cls.id)
  if (!row) return false
  return row.enteredVehicleId !== '' && row.enteredVehicleId !== null && row.enteredVehicleId !== undefined
}

/** Whether an already-entered class is paid - only meaningful combined with isEntered() above
 *  (an unentered class has no row.paid worth reading). Separate lookup, same reasoning as
 *  SignupRowDetail's own `signup.classes.find(...)` - these arrays are a handful of rows, so a
 *  second find() per row costs nothing and keeps each helper's contract simple. */
function isPaidEntry(cls: EventPageClass, signup: SignupState | null): boolean {
  if (!signup) return false
  const row = signup.classes.find((c) => c.id === cls.id)
  return row ? truthy(row.paid) : false
}

/** Orange check-circle from the mockup's per-row "entered" marker, reused here at larger size for
 *  the summary card's own header icon (MobileClasses.dc.html's "You're entered in 2 classes"). */
function CheckCircleIcon() {
  return (
    <span className={styles.statusIcon}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12l5 5L20 7" />
      </svg>
    </span>
  )
}

/** "You're entered in N classes" card at the top of the mobile Classes list
 *  (MobileClasses.dc.html's own top-of-list card, ported literally) - the racer's whole paid/unpaid
 *  picture at a glance, above the per-row detail further down. Mobile only: desktop already carries
 *  the equivalent summary in RegistrationCard's "N classes selected / $total" price line, pinned in
 *  the rail the whole time the racer's scrolling, so repeating it atop the table would just be
 *  noise there - same per-breakpoint split the entered-highlight and footer-bar treatments above
 *  already use. */
function EntryStatusCard({ summary, dollarType, canAddNewEntry }: { summary: ReturnType<typeof entryStatusSummary>; dollarType: string; canAddNewEntry: boolean }) {
  const label = unpaidEntryLabel({ classCount: summary.classCount, extraCount: summary.extraCount })
  const paidAmount = formatMoney(summary.paidTotal, dollarType || 'USD')
  const unpaidAmount = formatMoney(summary.unpaidTotal, dollarType || 'USD')
  return (
    <div className={styles.statusCard}>
      <div className={styles.statusHeader}>
        <CheckCircleIcon />
        You're entered in {label}
      </div>
      <div className={styles.statusPills}>
        {summary.paidCount > 0 ? (
          <span className={styles.statusPillPaid}>
            ✓ {summary.paidCount} paid{paidAmount ? ` · ${paidAmount}` : ''}
          </span>
        ) : null}
        {summary.unpaidCount > 0 ? (
          <span className={styles.statusPillUnpaid}>
            {summary.unpaidCount} unpaid{unpaidAmount ? ` · ${unpaidAmount} due` : ''}
          </span>
        ) : null}
      </div>
      {/* Only meaningful while new entries can still be added - once registration's closed this
          status card is a read-only summary, not an invitation to tick more classes below. */}
      {canAddNewEntry ? <div className={styles.statusHelper}>Tick more classes below to add them to your entry.</div> : null}
    </div>
  )
}

function groupByType(classes: EventPageClass[]): Array<[string, EventPageClass[]]> {
  const order: string[] = []
  const groups = new Map<string, EventPageClass[]>()
  for (const c of classes) {
    const key = c.typeName || 'Other'
    if (!groups.has(key)) {
      groups.set(key, [])
      order.push(key)
    }
    groups.get(key)!.push(c)
  }
  return order.map((key) => [key, groups.get(key)!])
}

/** Same event-wide gate as events/displayEvent.cfm:713-737 (see eventPage.cfc) - every class shares
 *  one signup URL and one open/closed/full state; only `disabled` varies per class. A disabled
 *  class shows no action HERE, matching events/displayEvent.cfm:708-712 - the legacy page's own
 *  action-button DISABLED markup (`<a class="btn btn-danger">...DISABLED</a>`) is genuinely
 *  dead/commented-out code there. That's not the same as showing nothing at all, though: the
 *  legacy page DOES surface a real, live "DISABLED" badge for these classes - just next to the
 *  class NAME (line 663's `badge bg-danger`), not in this action column. See the name-line badge
 *  in nameBlock below for that (was missing here until a racer flagged a disabled class at event
 *  16498 rendering as a blank row with no explanation at all). Anonymous visitors and track-type
 *  viewers always see this plain status/link - the interactive checkbox below is only for a
 *  logged-in racer.
 *
 *  No per-class "Sign up" link anymore (an explicit ask) - RegistrationCard's own signupUrl
 *  button already covers the exact same anonymous/track-viewer case at the page level, so a
 *  second link repeated on every open row was redundant. "Closed" stays - it's a real per-class-
 *  row status, not an action, and isn't shown anywhere else at that granularity. */
function ClassAction({ cls, event }: { cls: EventPageClass; event: EventPageEvent }) {
  if (truthy(cls.disabled)) return null
  if (!truthy(event.signupWindowOpen)) {
    return <span className={`${styles.action} ${styles.closed}`}>Closed</span>
  }
  return null
}

/** Car icon from Desktop.dc.html/MobileClasses.dc.html's "Car" pill, reproduced verbatim. */
function CarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M2 15v-3l3-4h9l4 4h3v3h-2M8 15h7" />
      <circle cx="6" cy="16" r="2" />
      <circle cx="17" cy="16" r="2" />
    </svg>
  )
}

/** The leading checkbox cell - bare, no visible label, exactly matching Desktop.dc.html/
 *  MobileClasses.dc.html's `<label><input type="checkbox" aria-label="Enter {{c.name}}"></label>`
 *  (a cell to the left of the class name, not inline text in the meta area). Always rendered (even
 *  as an empty placeholder) when signup UI is active, so the desktop grid's columns stay aligned
 *  across every row regardless of that row's state. */
function SignupCheckboxCell({
  cls,
  signup,
  onOpen,
}: {
  cls: EventPageClass
  signup: SignupState
  onOpen: () => void
}) {
  const row = signup.classes.find((c) => c.id === cls.id)
  if (!row) return <div className={styles.checkboxCell} /> // disabled classes aren't in getRacerSignup's result

  const entered = row.enteredVehicleId !== '' && row.enteredVehicleId !== null && row.enteredVehicleId !== undefined
  const capReached = signup.classesPerRacer !== '' && signup.totalEntries >= Number(signup.classesPerRacer)
  const disabled = entered || truthy(row.blocked) || truthy(row.full) || capReached

  return (
    <label className={styles.checkboxCell}>
      <input type="checkbox" checked={entered} disabled={disabled} aria-label={`Enter ${cls.name}`} onChange={() => !disabled && onOpen()} />
    </label>
  )
}

/** Below the class name: the "Car" pill for an entered class (icon + vehicle name, matching the
 *  mockup's confirmed-car style exactly - solid border, not the dashed "needs a car" variant,
 *  since our flow never leaves a class checked without a vehicle) - clickable to change vehicles,
 *  plus a Remove link, both only while the entry is still unpaid. Otherwise, a short reason why
 *  the checkbox is disabled - the mockup has no equivalent for that, since it never modeled real
 *  blocked/full/cap rules. */
function SignupRowDetail({
  cls,
  event,
  signup,
  canEdit,
  onChangeVehicle,
  onRemoved,
}: {
  cls: EventPageClass
  event: EventPageEvent
  signup: SignupState
  // Whether changing vehicle / removing is still allowed - i.e. canAddNewEntry from ClassList.
  // The entry's own display (car name, paid/unpaid badge) always shows regardless of this; only
  // the edit controls are gated, since the backend doesn't gate updateClassEntryVehicle/
  // removeClassEntry on the signup window either and this is the only thing stopping a racer from
  // editing a locked entry once registration's closed.
  canEdit: boolean
  onChangeVehicle: () => void
  onRemoved: (next: SignupState) => void
}) {
  const [confirming, setConfirming] = useState(false)
  const [removing, setRemoving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const row = signup.classes.find((c) => c.id === cls.id)
  if (!row) return null

  const entered = row.enteredVehicleId !== '' && row.enteredVehicleId !== null && row.enteredVehicleId !== undefined
  if (entered) {
    const vehicle = signup.vehicles.find((v) => v.id === Number(row.enteredVehicleId))
    const paid = truthy(row.paid)
    const remove = () => {
      setRemoving(true)
      setError(null)
      postRemoveClassEntry(event.id, cls.id)
        .then((next) => {
          // Reset here too, not just in .catch() - this component instance isn't remounted when
          // the class becomes entered again (same list position, same cls.id), so a stale
          // removing/confirming=true left behind here sat dormant (irrelevant while `entered` was
          // false) and then resurfaced as a permanently-stuck "Removing…" confirm UI the next time
          // this same class got re-entered. Same bug class as SponsorsAndExtras.tsx's
          // ExtraControls (its `saving`/`removing`/`confirming` reset in save()/remove()) -
          // confirmed live: add a class, remove it, add it again -> stuck "Removing…" pill.
          setRemoving(false)
          setConfirming(false)
          onRemoved(next)
        })
        .catch((err: unknown) => {
          setError(err instanceof Error ? err.message : String(err))
          setRemoving(false)
          setConfirming(false)
        })
    }

    // Inline confirm instead of window.confirm()/alert() - a native dialog blocks the whole page
    // until dismissed (nothing else here uses one; every other confirmation in this app is a
    // custom-styled control, e.g. VehicleSignupModal's own Cancel/Confirm).
    if (confirming) {
      return (
        <div className={styles.carPill}>
          <span className={styles.confirmText}>Remove your entry in {cls.name}?</span>
          <button type="button" className={styles.confirmYes} onClick={remove} disabled={removing}>
            {removing ? 'Removing…' : 'Yes'}
          </button>
          <button type="button" className={styles.confirmNo} onClick={() => setConfirming(false)} disabled={removing}>
            Cancel
          </button>
        </div>
      )
    }

    return (
      <>
        <div className={styles.carPill}>
          <CarIcon />
          {paid || !canEdit ? (
            <span className={styles.carName}>{vehicle ? vehicle.name : 'Entered'}</span>
          ) : (
            <button type="button" className={styles.carNameBtn} onClick={onChangeVehicle}>
              {vehicle ? vehicle.name : 'Entered'}
            </button>
          )}
          {paid ? (
            <span className={styles.paidBadge}>PAID</span>
          ) : (
            formatMoney(row.amountDue, event.dollarType || 'USD') && <span className={styles.entryPrice}>{formatMoney(row.amountDue, event.dollarType || 'USD')}</span>
          )}
          {!paid && canEdit ? (
            <button type="button" className={styles.removeBtn} onClick={() => setConfirming(true)} aria-label={`Remove entry in ${cls.name}`}>
              ×
            </button>
          ) : null}
        </div>
        {error ? <div className={styles.signupReason}>{error}</div> : null}
      </>
    )
  }

  const capReached = signup.classesPerRacer !== '' && signup.totalEntries >= Number(signup.classesPerRacer)
  let reason = ''
  if (truthy(row.blocked)) reason = "Can't enter - blocked by another entry"
  else if (truthy(row.full)) reason = 'This class is full'
  else if (capReached) reason = "You've reached the max classes for this event"

  return reason ? <div className={styles.signupReason}>{reason}</div> : null
}

export function ClassList({
  classes,
  event,
  viewer,
  signup,
  isMobile,
  onSignupChange,
}: {
  classes: EventPageClass[]
  event: EventPageEvent
  viewer: EventPageViewer
  signup: SignupState | null
  isMobile: boolean
  onSignupChange: (next: SignupState) => void
}) {
  const [activeModal, setActiveModal] = useState<ActiveModal | null>(null)

  if (!classes.length) return null

  // A logged-in racer gets the interactive checkbox only while signup is open and the event isn't
  // full - everyone else (anonymous, track viewers) keeps the plain link/closed state exactly as
  // before. This gates adding a *new* entry only; it must NOT gate whether an *already-made* entry
  // still shows its paid/unpaid status once the window closes (mockup note 10: "withdraw happens
  // in Manage entry, not by unticking" - an entered class keeps its own display regardless of
  // whether new signups are still possible). That was this component's actual bug: event 16489,
  // racer 60349 - a closed window hid a real paid entry entirely instead of just locking it.
  const canAddNewEntry = viewer.type === 'racer' && truthy(viewer.loggedIn) && truthy(event.signupWindowOpen) && !truthy(event.full)
  // Whether this logged-in racer has a signup snapshot loaded at all, independent of canAddNewEntry
  // above. `signup` can briefly be null right after mount while its own fetch is in flight -
  // checked separately (with `signup` itself in the JSX condition below) so TS narrows it there.
  const isRacerViewer = viewer.type === 'racer' && truthy(viewer.loggedIn)
  const statusSummary = isRacerViewer && signup ? entryStatusSummary(signup) : null

  return (
    <div className={styles.list}>
      {isMobile && statusSummary && statusSummary.classCount + statusSummary.extraCount > 0 ? (
        <EntryStatusCard summary={statusSummary} dollarType={event.dollarType} canAddNewEntry={canAddNewEntry} />
      ) : null}
      {groupByType(classes).map(([type, rows]) => (
        <div key={type} className={styles.group}>
          <h3 className={styles.groupLabel}>{type}</h3>
          <div className={styles.rows}>
            {rows.map((c) => {
              const signupUiOn = isRacerViewer && signup
              // Highlight a card the racer has already entered - Desktop.dc.html tints the row's
              // background (`#fff7ef`, this app's --bg-accent-tint), MobileClasses.dc.html instead
              // gives the card a colored border - matching each breakpoint's own mockup literally.
              // Based on actual entered state only, never on canAddNewEntry, so this keeps showing
              // after the window closes.
              const entered = signupUiOn ? isEntered(c, signup) : false
              // Mobile-only extra step on top of the above: a PAID entered class gets the green
              // treatment instead of orange (requested on top of the mockup, which doesn't itself
              // distinguish paid/unpaid at the card level - only the badge inside is colored there).
              const paidEntry = entered && isMobile ? isPaidEntry(c, signup) : false
              // The checkbox cell itself is only worth rendering for a class that's either already
              // entered (shows checked+locked) or still open to a new entry - otherwise there's
              // nothing to click and ClassAction's plain "Closed" text covers that row instead
              // (entriesAndAction below).
              const showCheckbox = signupUiOn && (entered || canAddNewEntry)
              const nameBlock = (
                <div className={styles.main}>
                  <div className={styles.nameLine}>
                    <span className={styles.name}>{c.name}</span>
                    {/* The real legacy behavior (events/displayEvent.cfm:663, a Bootstrap
                        `badge bg-danger`) - visible to every viewer, not just a logged-in racer.
                        Only the legacy *action-button* DISABLED markup was dead/commented-out
                        code (ClassAction below correctly renders nothing there, matching that) -
                        this name-line badge is the real, non-dead indicator and was missed when
                        ClassAction's comment assumed the whole disabled-row treatment was dead. */}
                    {truthy(c.disabled) ? <span className={styles.disabledBadge}>DISABLED</span> : null}
                    {isMobile && c.format ? <span className={styles.format}>{c.format}</span> : null}
                  </div>
                  {/* Rich text (the legacy page runs it through paragraphFormat() but never
                      escapes it) - same trust boundary as event.description, see App.tsx. */}
                  {/* eslint-disable-next-line react/no-danger */}
                  {c.rules ? <div className={styles.rules} dangerouslySetInnerHTML={{ __html: sanitizeRichText(c.rules) }} /> : null}
                  {signupUiOn ? (
                    <SignupRowDetail
                      cls={c}
                      event={event}
                      signup={signup}
                      canEdit={canAddNewEntry}
                      onChangeVehicle={() => {
                        const row = signup.classes.find((x) => x.id === c.id)
                        setActiveModal({
                          cls: c,
                          mode: 'change',
                          currentVehicleId: row ? Number(row.enteredVehicleId) : undefined,
                          currentAmountDue: row?.amountDue,
                        })
                      }}
                      onRemoved={onSignupChange}
                    />
                  ) : null}
                </div>
              )
              const entriesAndAction = (
                <>
                  {!truthy(event.hideEntries) ? (
                    <a href={`/events/displayEntries.cfm?id=${event.id}&cid=${c.id}`} className={styles.entriesLink}>
                      Entries
                    </a>
                  ) : null}
                  {/* Covers both the non-racer viewers (signupUiOn false) and a logged-in racer's
                      not-yet-entered row once the window's closed/full (showCheckbox false, entered
                      false) - same "Closed"/"Sign up"/full text either way. */}
                  {!entered && !canAddNewEntry ? <ClassAction cls={c} event={event} /> : null}
                </>
              )

              return isMobile ? (
                <div key={c.id} className={`${styles.row} ${entered ? (paidEntry ? styles.rowEnteredPaid : styles.rowEntered) : ''}`}>
                  <div className={styles.rowBody}>
                    {showCheckbox ? <SignupCheckboxCell cls={c} signup={signup} onOpen={() => setActiveModal({ cls: c, mode: 'add' })} /> : null}
                    {nameBlock}
                  </div>
                  <div className={styles.meta}>
                    <span className={styles.entered}>{c.entered} entered</span>
                    {entriesAndAction}
                  </div>
                </div>
              ) : (
                <div key={c.id} className={`${styles.rowGrid} ${entered ? styles.rowGridEntered : ''}`}>
                  {showCheckbox ? (
                    <SignupCheckboxCell cls={c} signup={signup} onOpen={() => setActiveModal({ cls: c, mode: 'add' })} />
                  ) : (
                    <div className={styles.checkboxCell} />
                  )}
                  {nameBlock}
                  <div className={styles.formatCol}>{c.format}</div>
                  <div className={styles.enteredCol}>{c.entered} entered</div>
                  <div className={styles.actionCol}>{entriesAndAction}</div>
                </div>
              )
            })}
          </div>
        </div>
      ))}

      {activeModal && signup ? (
        <VehicleSignupModal
          cls={activeModal.cls}
          event={event}
          signup={signup}
          mode={activeModal.mode}
          currentVehicleId={activeModal.currentVehicleId}
          currentAmountDue={activeModal.currentAmountDue}
          onClose={() => setActiveModal(null)}
          onSaved={(next) => {
            onSignupChange(next)
            setActiveModal(null)
          }}
        />
      ) : null}
    </div>
  )
}
